import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "../../chatbot.css";

import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";

import ReactECharts from "echarts-for-react";

import { QUICK_PROMPTS } from "./chatKnowledge";
import { getAssistantReply } from "./chatService";
import { normalizeChatPayload, isNormalizedChatPayload } from "./widgetRenderer";

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  text: "Hi! I can help you use Kerry Taste Trends—dashboards, filters, quarterly trends, and more. What would you like to know?",
};

const DEFAULT_VISUALIZATION = {
  type: "none",
  title: null,
  x_axis: null,
  y_axis: null,
  series: [],
};


// ============================================================
// RESPONSE NORMALIZATION
// ============================================================

function normalizeResponse(result) {
  if (typeof result === "string") {
    return normalizeChatPayload(result);
  }

  if (isNormalizedChatPayload(result)) {
    return result;
  }

  return normalizeChatPayload(result);
}


// ============================================================
// FORMATTING HELPERS
// ============================================================

function formatLabel(value) {
  if (!value) return "";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isCalendarYear(value) {
  const year = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isInteger(year) && year >= 1900 && year <= 2100;
}

function formatAxisTick(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || isCalendarYear(number)) {
    return formatValue(value);
  }

  const sign = number < 0 ? "-" : "";
  const absolute = Math.abs(number);
  const compact = (amount, suffix) => {
    const rounded = Math.round(amount * 10) / 10;
    const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
    return `${sign}${text}${suffix}`;
  };

  if (absolute >= 10000000) return compact(absolute / 10000000, "Cr");
  if (absolute >= 100000) return compact(absolute / 100000, "L");
  if (absolute >= 1000) return compact(absolute / 1000, "k");
  return formatValue(number);
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }

  if (typeof value === "number") {
    if (isCalendarYear(value)) return String(value);
    return new Intl.NumberFormat("en-IN", {
      maximumFractionDigits: 2,
    }).format(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  return String(value).replace(/(^|[^\w,.])([1-9]),(\d{3})\b(?![,.]\d)/g, (match, prefix, head, tail) => {
    const year = Number(`${head}${tail}`);
    return isCalendarYear(year) ? `${prefix}${year}` : match;
  });
}


// ============================================================
// KPI CARDS
// Expected API format:
// kpis: [{ label: "Restaurants", value: 45000 }]
// ============================================================

function KpiCards({ kpis }) {
  if (!Array.isArray(kpis) || kpis.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 grid grid-cols-2 gap-2">
      {kpis.map((kpi, index) => (
        <div
          key={`${kpi.label || "kpi"}-${index}`}
          className="min-w-0 rounded-xl border border-slate bg-white p-3"
        >
          <p className="mb-1 text-xs text-gray">
            {kpi.label || "Metric"}
          </p>

          <p className="break-words text-lg font-semibold text-black">
            {formatValue(kpi.value)}
            {kpi.unit === "percent" ? "%" : ""}
            {kpi.unit === "percentage_points" ? " pp" : ""}
          </p>

          {kpi.change !== undefined &&
            kpi.change !== null && (
              <p className="mt-1 text-xs text-gray">
                Change: {formatValue(kpi.change)}
                {kpi.change_percentage !== undefined &&
                  kpi.change_percentage !== null &&
                  ` (${formatValue(kpi.change_percentage)}%)`}
              </p>
            )}
        </div>
      ))}
    </div>
  );
}


// ============================================================
// CHART CONFIGURATION
// Supports bar, line, and pie charts.
// ============================================================
function buildChartOption(visualization) {
  if (
    !visualization ||
    !Array.isArray(visualization.series) ||
    visualization.series.length === 0
  ) {
    return null;
  }

  const chartType = visualization.type;
  const COMPARE_COLORS = ["#7CA76F", "#3E6B8A"];

  if (visualization.layout === "compare") {
    const periods = (visualization.xAxis || visualization.x_axis)?.data || [];
    const values = (visualization.series[0]?.data || []).map((point) =>
      point != null && typeof point === "object" ? point.value ?? null : point
    );

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          const escapeHtml = (value) =>
            String(value ?? "").replace(/[&<>"']/g, (char) => ({
              "&": "&amp;",
              "<": "&lt;",
              ">": "&gt;",
              '"': "&quot;",
              "'": "&#39;",
            }[char]));
          return `${escapeHtml(point?.name)}<br/>${escapeHtml(formatValue(point?.value))}`;
        },
      },
      grid: { left: 8, right: 64, top: 8, bottom: 4, containLabel: true },
      xAxis: {
        type: "value",
        splitNumber: 3,
        axisLabel: {
          hideOverlap: true,
          margin: 12,
          formatter: (value) => formatAxisTick(value),
        },
        splitLine: { lineStyle: { color: "#ececec" } },
      },
      yAxis: {
        type: "category",
        data: periods.map((period) => formatValue(period)),
        inverse: true,
        axisTick: { show: false },
        axisLabel: { formatter: (value) => formatValue(value) },
      },
      series: [
        {
          type: "bar",
          barWidth: 16,
          data: values.map((value, index) => ({
            value,
            itemStyle: {
              color: COMPARE_COLORS[index % COMPARE_COLORS.length],
              borderRadius: [0, 6, 6, 0],
            },
          })),
          label: {
            show: true,
            position: "right",
            color: "#2d2d2d",
            formatter: (point) => formatValue(point.value),
          },
        },
      ],
    };
  }

  // ----------------------------------------------------------
  // BAR AND LINE CHARTS
  // ----------------------------------------------------------

  if (chartType === "bar" || chartType === "line") {
    const firstSeries = visualization.series[0];

    // Support both:
    // 1. Comparison: [{ label, value }, ...]
    // 2. Trends: [120, 150, 180, ...]
    // 3. Trends with explicit xAxis data.

    const configuredXAxis =
      visualization.xAxis || visualization.x_axis;

    const configuredYAxis =
      visualization.yAxis || visualization.y_axis;

    const labels = Array.isArray(configuredXAxis?.data)
      ? configuredXAxis.data
      : Array.isArray(firstSeries.data)
        ? firstSeries.data.map((point) =>
            typeof point === "object" && point !== null
              ? point.label
              : undefined
          )
        : [];

    const series = visualization.series.map((item) => {
      const rawData = Array.isArray(item.data) ? item.data : [];

      const values = rawData.map((point) => {
        if (point === null || point === undefined) {
          return null;
        }

        // Existing comparison format.
        if (typeof point === "object") {
          return point.value ?? null;
        }

        // New cuisine trends format.
        return point;
      });

      const colorEachBar = chartType === "bar" && visualization.series.length === 1;

      return {
        name: item.name || "Value",
        type: chartType,
        colorBy: colorEachBar ? "data" : "series",
        smooth:
          item.smooth !== undefined
            ? item.smooth
            : chartType === "line",
        showSymbol:
          item.showSymbol !== undefined
            ? item.showSymbol
            : chartType === "line",
        connectNulls:
          item.connectNulls !== undefined
            ? item.connectNulls
            : false,
        barMaxWidth: chartType === "bar" ? 45 : undefined,
        data: values,
      };
    });

    const multipleSeries = series.length > 1;

    return {
      title: {
        show: false,
      },

      tooltip: visualization.tooltip || {
        trigger: "axis",
      },

      legend: visualization.legend || (
        multipleSeries
          ? { type: "scroll", bottom: 0 }
          : { show: false }
      ),

      grid: {
        left: 60,
        right: 20,
        bottom: multipleSeries ? 65 : 48,
        top: 24,
        containLabel: true,
      },

      xAxis: {
        type: configuredXAxis?.type || "category",
        name:
          configuredXAxis?.name ||
          configuredXAxis?.nameText ||
          (typeof configuredXAxis === "string"
            ? configuredXAxis
            : ""),
        boundaryGap:
          configuredXAxis?.boundaryGap ??
          (chartType === "bar"),
        data: labels.map((label) => formatValue(formatLabel(label))),
        axisLabel: {
          interval: 0,
          rotate: labels.length > 4 ? 28 : 0,
          width: 90,
          overflow: "break",
          formatter: (value) => formatValue(value),
        },
      },

      yAxis: {
        type: configuredYAxis?.type || "value",
        name:
          configuredYAxis?.name ||
          (typeof configuredYAxis === "string"
            ? configuredYAxis
            : ""),
        min: configuredYAxis?.min,
        axisLabel: {
          formatter: (value) => formatValue(value),
        },
      },

      series,
    };
  }

  // ----------------------------------------------------------
  // PIE CHARTS
  // ----------------------------------------------------------

  if (chartType === "pie") {
    const pieData = visualization.series.flatMap((item) =>
      (item.data || []).map((point) => ({
        name: point.label ?? point.name,
        value: point.value,
      }))
    );

    return {
      tooltip: {
        trigger: "item",
        formatter: "{b}: {c} ({d}%)",
      },

      legend: {
        bottom: 0,
      },

      series: [
        {
          name: visualization.title || "Distribution",
          type: "pie",
          radius: ["35%", "68%"],
          center: ["50%", "45%"],
          data: pieData,
          label: {
            show: true,
            formatter: "{b}: {d}%",
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 12,
              shadowOffsetX: 0,
            },
          },
        },
      ],
    };
  }

  return null;
}
// function buildChartOption(visualization) {
//   if (
//     !visualization ||
//     !Array.isArray(visualization.series) ||
//     visualization.series.length === 0
//   ) {
//     return null;
//   }

//   const chartType = visualization.type;

//   // ----------------------------------------------------------
//   // BAR AND LINE
//   // ----------------------------------------------------------

//   if (chartType === "bar" || chartType === "line") {
//     const firstSeries = visualization.series[0];

//     const labels = Array.isArray(firstSeries.data)
//       ? firstSeries.data.map((point) => point.label)
//       : [];

//     const series = visualization.series.map((item) => ({
//       name: item.name || "Value",
//       type: chartType,
//       smooth: chartType === "line",
//       barMaxWidth: chartType === "bar" ? 45 : undefined,
//       data: (item.data || []).map((point) => point.value),
//     }));

//     return {
//       tooltip: {
//         trigger: "axis",
//       },

//       legend: {
//         top: 5,
//       },

//       grid: {
//         left: 50,
//         right: 20,
//         bottom: 65,
//         top: 50,
//         containLabel: true,
//       },

//       xAxis: {
//         type: "category",
//         name: visualization.x_axis || "",
//         data: labels,
//         axisLabel: {
//           interval: 0,
//           rotate: labels.length > 4 ? 25 : 0,
//         },
//       },

//       yAxis: {
//         type: "value",
//         name: visualization.y_axis || "",
//       },

//       series,
//     };
//   }

//   // ----------------------------------------------------------
//   // PIE
//   // ----------------------------------------------------------

//   if (chartType === "pie") {
//     const pieData = visualization.series.flatMap((item) =>
//       (item.data || []).map((point) => ({
//         name: point.label,
//         value: point.value,
//       }))
//     );

//     return {
//       tooltip: {
//         trigger: "item",
//         formatter: "{b}: {c} ({d}%)",
//       },

//       legend: {
//         bottom: 0,
//       },

//       series: [
//         {
//           name: visualization.title || "Distribution",
//           type: "pie",
//           radius: ["35%", "68%"],
//           center: ["50%", "45%"],
//           data: pieData,
//           label: {
//             show: true,
//             formatter: "{b}: {d}%",
//           },
//           emphasis: {
//             itemStyle: {
//               shadowBlur: 12,
//               shadowOffsetX: 0,
//             },
//           },
//         },
//       ],
//     };
//   }

//   return null;
// }


// ============================================================
// CHART COMPONENT
// ============================================================

class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

function ChatChart({ visualization, expanded = false }) {
  const option = buildChartOption(visualization);

  if (!option) {
    return null;
  }

  const height =
    visualization.layout === "compare" ? (expanded ? 168 : 132) : expanded ? 420 : 280;

  return (
    <div className="mt-3 min-w-0 rounded-xl border border-slate bg-white p-2">
      {visualization.title && (
        <div className="flex items-baseline justify-between gap-2 px-2 pt-2">
          <h4 className="text-sm font-semibold text-black">
            {formatValue(visualization.title)}
          </h4>
          {visualization.subtitle && (
            <p className="m-0 shrink-0 text-xs text-gray">
              {formatValue(visualization.subtitle)}
            </p>
          )}
        </div>
      )}

      <ReactECharts
        key={expanded ? "expanded" : "docked"}
        option={option}
        notMerge
        lazyUpdate
        style={{
          width: "100%",
          height,
        }}
      />
    </div>
  );
}


function ChatDataTable({ table }) {
  if (!table?.columns?.length) return null;

  return (
    <div className="mt-3 min-w-0 overflow-x-auto rounded-xl border border-slate bg-white p-3">
      {table.title && (
        <h4 className="mb-2 text-sm font-semibold text-black">
          {table.title}
        </h4>
      )}

      <table className="w-full min-w-[240px] text-left text-xs text-black">
        <thead>
          <tr className="border-b border-slate">
            {table.columns.map((col) => (
              <th key={col.key} className="px-2 py-2 font-semibold">
                {formatValue(col.label || formatLabel(col.key))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(table.rows || []).map((row, rowIndex) => (
            <tr key={rowIndex} className="border-b border-slate/60">
              {row.map((cell, cellIndex) => (
                <td key={cellIndex} className="px-2 py-2">
                  {formatValue(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ============================================================
// INSIGHTS
// ============================================================

function ChatInsights({ insights }) {
  if (!Array.isArray(insights) || insights.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 rounded-xl border border-slate bg-white p-3">
      <h4 className="mb-2 text-sm font-semibold text-black">
        Key Insights
      </h4>

      <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-black">
        {insights.map((insight, index) => {
          const text =
            typeof insight === "string"
              ? insight
              : insight?.text ||
                insight?.description ||
                JSON.stringify(insight);

          return <li key={index}>{text}</li>;
        })}
      </ul>
    </div>
  );
}


// ============================================================
// FOLLOW-UP QUESTIONS
// ============================================================

function FollowUpQuestions({ questions, onAsk }) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {questions.map((question, index) => (
        <button
          key={`${question}-${index}`}
          type="button"
          disabled={false}
          onClick={() => onAsk(question)}
          className="rounded-full border border-green bg-lgreen px-3 py-2 text-left text-xs text-black hover:opacity-80"
        >
          {question}
        </button>
      ))}
    </div>
  );
}


// ============================================================
// STRUCTURED ASSISTANT MESSAGE
// ============================================================

function groupVisualizations(visualizations) {
  const groups = [];

  visualizations.forEach((visualization) => {
    const previous = groups[groups.length - 1];
    if (visualization.layout === "compare" && previous?.kind === "compare") {
      previous.items.push(visualization);
      return;
    }

    groups.push({
      kind: visualization.layout === "compare" ? "compare" : "single",
      items: [visualization],
    });
  });

  return groups;
}

function AssistantResponse({ message, onAsk, expanded = false }) {
  const response = message.response;

  // Welcome message or a message without structured response.
  if (!response) {
    return <div className="whitespace-pre-wrap">{message.text}</div>;
  }

  return (
    <div className="min-w-0 w-full">
      <div className="whitespace-pre-wrap break-words">
        {response.reply || message.text}
      </div>

      <KpiCards kpis={response.kpis} />

      {groupVisualizations(
        (response.visualizations?.length
          ? response.visualizations
          : response.visualization
            ? [response.visualization]
            : []
        ).filter(
          (visualization) =>
            visualization &&
            visualization.type &&
            visualization.type !== "none" &&
            Array.isArray(visualization.series) &&
            visualization.series.length > 0
        )
      ).map((group, groupIndex) => (
        <div
          key={`${group.kind}-${groupIndex}`}
          className={
            group.kind === "compare" && expanded
              ? "grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
              : "flex flex-col"
          }
        >
          {group.items.map((visualization, index) => (
            <ChartErrorBoundary
              key={`${visualization.title || "chart"}-${groupIndex}-${index}`}
            >
              <ChatChart visualization={visualization} expanded={expanded} />
            </ChartErrorBoundary>
          ))}
        </div>
      ))}

      {(response.tables || []).map((table, index) => (
        <ChatDataTable key={`${table.title || "table"}-${index}`} table={table} />
      ))}

      <ChatInsights insights={response.insights} />

      <FollowUpQuestions
        questions={response.follow_up_questions}
        onAsk={onAsk}
      />

      {response.status === "error" && (
        <p className="mt-2 text-xs text-red-600" role="alert">
          The request could not be completed.
        </p>
      )}
    </div>
  );
}


// ============================================================
// MAIN CHATBOT
// ============================================================

function Chatbot() {
  const { role } = useSelector((state) => state.auth);

  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [conversation, setConversation] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth",
      });

      inputRef.current?.focus();
    }
  }, [isOpen, messages, isTyping]);

  useEffect(() => {
    if (!isOpen || !isFullscreen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsFullscreen(false);
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, isFullscreen]);

  const appendMessage = (message) => {
    setMessages((prev) => [
      ...prev,
      {
        ...message,
        id: `${Date.now()}-${prev.length}`,
      },
    ]);
  };

  // ----------------------------------------------------------
  // SEND MESSAGE
  // ----------------------------------------------------------

  const sendMessage = async (text) => {
    const trimmed = text.trim();

    if (!trimmed || isTyping) return;

    // Build history before adding the current question.
    // The current question is sent separately as "message".
    const history = messages
      .filter(
        (item) =>
          item.id !== "welcome" &&
          (item.role === "user" || item.role === "assistant")
      )
      .map((item) => ({
        role: item.role,
        content: item.text,
      }));

    appendMessage({
      role: "user",
      text: trimmed,
    });

    setInput("");
    setIsTyping(true);

    try {
      const result = await getAssistantReply(trimmed, {
        role,
        history,
        conversation,
      });

      const structuredResponse = normalizeResponse(result);

      if (structuredResponse.conversation) {
        setConversation(structuredResponse.conversation);
      }

      appendMessage({
        role: "assistant",
        text: structuredResponse.reply,
        response: structuredResponse,
      });
    } catch (error) {
      console.error("Chatbot error:", error);

      appendMessage({
        role: "assistant",
        text: "Sorry, something went wrong. Please try again.",
        response: {
          status: "error",
          intent: "unknown",
          reply: "Sorry, something went wrong. Please try again.",
          kpis: [],
          data: {},
          visualization: DEFAULT_VISUALIZATION,
          insights: [],
          follow_up_questions: [],
          sources: [],
        },
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(input);
  };

  // ----------------------------------------------------------
  // CLOSED CHATBOT
  // ----------------------------------------------------------

  if (!isOpen) {
    return (
      <button
        type="button"
        aria-label="Open Taste Trends assistant"
        className="fixed bottom-6 right-6 z-[200] flex h-14 w-14 items-center justify-center rounded-full bg-green text-white shadow-xl hover:opacity-90"
        onClick={() => setIsOpen(true)}
      >
        <ChatIcon fontSize="medium" />
      </button>
    );
  }

  // ----------------------------------------------------------
  // OPEN CHATBOT
  // ----------------------------------------------------------

  return (
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[300] flex h-dvh w-screen flex-col overflow-hidden bg-white"
          : "fixed bottom-6 right-6 z-[200] flex w-[min(100vw-2rem,420px)] flex-col overflow-hidden rounded-2xl border border-slate bg-white shadow-2xl"
      }
      role="dialog"
      aria-label="Taste Trends assistant"
      aria-modal={isFullscreen || undefined}
    >
      {/* HEADER */}

      <div className="flex shrink-0 items-center justify-between bg-green px-4 py-3 text-white">
        <div className="flex items-center gap-2">
          <SmartToyOutlinedIcon fontSize="small" />

          <div>
            <p className="m-0 text-sm font-semibold leading-tight">
              Taste Trends Assistant
            </p>

            <p className="m-0 text-xs opacity-90">
              Ask about dashboards &amp; KTT data
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            aria-pressed={isFullscreen}
            className="rounded p-1 hover:bg-white/20"
            onClick={() => setIsFullscreen((current) => !current)}
          >
            {isFullscreen ? (
              <CloseFullscreenIcon fontSize="small" />
            ) : (
              <OpenInFullIcon fontSize="small" />
            )}
          </button>

          <button
            type="button"
            aria-label="Close assistant"
            className="rounded p-1 hover:bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* MESSAGES */}

      <div
        className={
          isFullscreen
            ? "flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-light px-4 py-4 md:px-8"
            : "flex max-h-[min(500px,calc(100vh_-_10rem))] min-h-[280px] flex-col gap-3 overflow-y-auto bg-light px-3 py-4"
        }
        aria-live="polite"
      >
        <div
          className={
            isFullscreen
              ? "mx-auto flex w-full max-w-6xl flex-col gap-3"
              : "contents"
          }
        >
        {messages.map((message) => {
          const isUser = message.role === "user";

          return (
            <div
              key={message.id}
              className={`flex gap-2 ${
                isUser ? "flex-row-reverse" : "flex-row"
              }`}
            >
              {/* AVATAR */}

              <div
                className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                  isUser
                    ? "bg-green text-white"
                    : "bg-white text-green shadow-md"
                }`}
              >
                {isUser ? (
                  <PersonOutlineIcon sx={{ fontSize: 16 }} />
                ) : (
                  <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </div>

              {/* MESSAGE CONTENT */}

              <div
                className={`min-w-0 rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                  isUser
                    ? "max-w-[90%] rounded-br-md bg-green text-white"
                    : `rounded-bl-md border border-slate bg-white text-black ${
                        isFullscreen ? "w-full" : "w-full max-w-[90%]"
                      }`
                }`}
              >
                {isUser ? (
                  <div className="whitespace-pre-wrap break-words">
                    {message.text}
                  </div>
                ) : (
                  <AssistantResponse
                    message={message}
                    onAsk={sendMessage}
                    expanded={isFullscreen}
                  />
                )}
              </div>
            </div>
          );
        })}

        {/* TYPING INDICATOR */}

        {isTyping && (
          <div className="flex gap-2">
            <div className="mt-1 flex h-7 w-7 items-center justify-center rounded-full bg-white text-green shadow-md">
              <SmartToyOutlinedIcon sx={{ fontSize: 16 }} />
            </div>

            <div className="rounded-2xl rounded-bl-md border border-slate bg-white px-3 py-2 text-sm text-gray">
              Analyzing KTT data…
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
        </div>
      </div>

      {/* QUICK PROMPTS */}

      {messages.length <= 1 && (
        <div className="shrink-0 border-t border-slate bg-white px-3 py-2">
          <div className={`flex flex-wrap gap-2 ${isFullscreen ? "mx-auto w-full max-w-6xl" : ""}`}>
          {QUICK_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              disabled={isTyping}
              className="rounded-full border border-green bg-lgreen px-3 py-1 text-left text-xs text-black hover:opacity-80 disabled:opacity-50"
              onClick={() => sendMessage(prompt)}
            >
              {prompt}
            </button>
          ))}
          </div>
        </div>
      )}

      {/* INPUT */}

      <form
        onSubmit={handleSubmit}
        className="flex shrink-0 items-center gap-2 border-t border-slate bg-white p-3"
      >
        <div className={`flex w-full items-center gap-2 ${isFullscreen ? "mx-auto max-w-6xl" : ""}`}>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="Ask about KTT data…"
          className="min-w-0 flex-1 rounded-full border border-slate px-4 py-2 text-sm text-black outline-none focus:border-green"
          disabled={isTyping}
          maxLength={500}
        />

        <button
          type="submit"
          aria-label="Send message"
          disabled={!input.trim() || isTyping}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          <SendIcon fontSize="small" />
        </button>
        </div>
      </form>
    </div>
  );
}

export default Chatbot;
